import Dexie from 'dexie';

import db from './db';
import { currentProfileId } from './meta';
import { normalizeFeedMessageRow } from '../feed/echo/messageActions';

const CHANNEL_EDITOR_PREFIX = 'channel-';
// Bounds the newest-first index walk: Arrow-Up targets recent messages, so a match
// deeper than this is not worth scanning the whole durable channel cache for.
//
// KNOWN GAP: this cap makes Arrow-Up a silent no-op when the viewer's newest own message
// sits deeper than the limit. It replaced an indexed seek — the legacy `messages` table
// carried [channel_id+author_id+sort_order], which found the target at any depth, and
// feed_messages has no author index to seek on. The gap is not covered by MessageFeed's
// in-memory session-window scan either: that scan only runs at the live tail, and it
// falls through to this lookup exactly when the viewer's own message is not in the loaded
// window — the same "busy channel, haven't posted in a while" case the cap drops. Fixing
// it properly means adding [channel_id+author_id+sort_order] to feed_messages (db version
// bump + one-time re-index) and deleting both the cap and the until() walk below.
const RECENT_MESSAGE_SCAN_LIMIT = 400;

export const channelIdFromEditorId = (editorId) => {
  if (!editorId || !editorId.startsWith(CHANNEL_EDITOR_PREFIX)) return null;
  const channelId = editorId.slice(CHANNEL_EDITOR_PREFIX.length);
  return channelId || null;
};

const getCurrentAuthorIdentity = () => {
  const profileId = currentProfileId();
  if (profileId) return { authorId: profileId, authorType: 'profile' };

  return { authorId: null, authorType: null };
};

// Shared eligibility rule for Arrow-Up edit targets, used by both the in-memory
// session-window scan (MessageFeed) and the durable fallback below.
export const isEditableOwnMessageRow = (row, authorId, authorType) =>
  Boolean(row) && !row.pseudotype && row.author_type === authorType && String(row.author_id) === String(authorId);

// Durable fallback behind the feed session window: reads the feed cache, so messages
// authored on another device or loaded through feed history are found even when the
// legacy messages table is empty.
export const findRecentEditableOwnMessage = async (channelId) => {
  if (!channelId) return null;

  const { authorId, authorType } = getCurrentAuthorIdentity();
  if (!authorId || !authorType) return null;

  let scannedRowCount = 0;
  const recentMessage = await db.feed_messages
    .where('[channel_id+sort_order]')
    .between([channelId, Dexie.minKey], [channelId, Dexie.maxKey], false, true)
    .reverse()
    .until(() => {
      scannedRowCount += 1;
      return scannedRowCount > RECENT_MESSAGE_SCAN_LIMIT;
    })
    .filter((message) => isEditableOwnMessageRow(message, authorId, authorType))
    .first();

  if (!recentMessage?.id) return null;
  return normalizeFeedMessageRow(recentMessage);
};
